import React from 'react';
import { AudioOutlined, FileExcelOutlined, FileImageOutlined, FileOutlined, FilePdfOutlined, FilePptOutlined, FileWordOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { getString } from '@nackle/intl-tools';
import { getDefaultCourseThumbnail, getDefaultCurriculumThumbnail } from '../../../const';
import { Navigation } from '../../../navigation';

export const DefaultAccessor = ( item ) => ( { 
    id: item.id,
    displayName: item.name?.replace( /^\*/, '' ) ?? '???????',
    name: item.name,
    description: item.description,
    tags: item?.lxp?.tags?.map( t => t.name ) ?? item.tags,
    getThumbnail: () => item.thumbnail,
    originalProps: item,
} );

export const CourseDataAccessor = ( course ) => ( {
    ...DefaultAccessor( course ),

    __type: 'course', // used only internally
    type: getString( 'courseList.type.course' ),
    getThumbnail: ( theme ) => {
        if ( course.thumbnail ) {return course.thumbnail;}
        return getDefaultCourseThumbnail( theme );
    },
    certificate: course.certificate?.link,
    hasCertificate: course.certificate?.hasCertificate,
    fetchCertificateUrl: course.fetchCertificateUrl,
    onClick: ( fn ) => {
        if ( course.blockClick ) {
            fn?.();
            return;
        }
        Navigation.push( `/course/${ course.id }` );
    },
    completed: course.status == 'COMPLETED',
    progress: course.progress,
    status: course.status,
    actionText: course.status == 'COMPLETED'
            ? 'course.actions.review' 
            : 'course.actions.launch',
    typeText: 'courseList.type.course',
    prerequisiteData: course.prerequisiteData,
    blockClick: course.blockClick,
} );

export const CurriculumDataAccessor = ( curriculum ) => ( {
    ...DefaultAccessor( curriculum ),

    __type: 'curriculum',
    type: getString( 'courseList.type.curriculum' ),
    getThumbnail: ( theme ) => {
        if ( curriculum.thumbnail ) {return curriculum.thumbnail;}
        return getDefaultCurriculumThumbnail( theme );
    },
    rating: curriculum.rating,
    onClick: () => {
        Navigation.push( `/curriculum/${ curriculum.id }` );
    },
    completed: curriculum.progress >= 100,
    progress: curriculum.progress,
    status: curriculum.progress >= 100 ? 'COMPLETED' : curriculum.progress <= 0 ? 'NOT_STARTED' : 'IN_PROGRESS',
    actionText: curriculum.progress >= 100
            ? 'curriculum.actions.review' 
            : 'curriculum.actions.explore',
    typeText: 'courseList.type.curriculum',
    prerequisiteData: curriculum.prerequisiteData,
} );

export const ResourceDataAccessor = ( resource ) => ( {
    ...DefaultAccessor( resource ),

    __type: 'resource',
    getThumbnail: undefined,
    getIcon: () => {

        if ( [ 'application/pdf' ].includes( resource.mimetype ) ) {
            return <FilePdfOutlined />;
        }
        
        if ( [ 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ].includes( resource.mimetype ) ) {
            return <FileWordOutlined />;
        }
        
        if ( [ 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ].includes( resource.mimetype ) ) {
            return <FileExcelOutlined />;
        }

        if ( [ 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ].includes( resource.mimetype ) ) {
            return <FilePptOutlined />;
        }
        
        if ( resource.mimetype.startsWith( 'image/' ) ) {
            return <FileImageOutlined />;
        }

        if ( resource.mimetype.startsWith( 'video/' ) ) {
            return <PlayCircleOutlined />;
        }

        if ( resource.mimetype.startsWith( 'audio/' ) ) {
            return <AudioOutlined />;
        }

        return <FileOutlined />;
    },
    onClick: ( callback ) => {
        resource.download( resource, callback );
    },
    mimetype: resource.mimetype,
    filename: resource.filename,
    actionText: 'resource.actions.download',
    type: getString( 'courseList.type.resource' ),
    typeText: 'courseList.type.resource',
} );

const dataAccessorMap = {
    curriculum: CurriculumDataAccessor,
    course: CourseDataAccessor,
    resource: ResourceDataAccessor
};

export const getDataAccessor = ( learningType ) => dataAccessorMap[ learningType ] ?? DefaultAccessor;